<template>

	<div class="row filter-group">
		<div class="col-buttons">
			<div class="row centered flex-start">
				<AppFilter
					v-for="(filter, index) in computedFilters"
					:key="index"
					:field-name="filter.text"
					:text="filter.finalValue || filter.text"
					:active-filter="filter.active"
					:toggle-cloud-popup="filter.toggleCloudPopup ?? true"
					@apply-filter="applyFilter(index)"
					@click-icon="resetFilter(index)"
					@filter-clicked="filterClicked(index)"
				>
					<slot v-if="filter.textSlot">
						<div class="col-12 centered">
							<AppTextInput
								:placeholder="filter.placeholder"
								:value="filter.value"
								@changing-text="v=>{ filter.callbackValue(v, index); }"
							/>
						</div>
					</slot>
					<slot v-if="filter.selectSlot">
						<div class="col-12 centered">
							<AppSelect
								relative
								:values="filter.values"
								@selected="v=>{ filter.callbackValue(v, index); }"
								:selected-index="filter.selectedIndex"
							/>
						</div>
					</slot>
					<slot v-if="filter.checklistSlot">
						<div class="col-12 centered">
							<AppChecklist
								relative
								:title="filter.text"
								:values="filter.values"
								@change-checked="v=>{ filter.callbackValue(v, index); }"
								:selected-indexes="filter.selectedIndexes"
							/>
						</div>
					</slot>
					<slot v-if="filter.priceSlot || filter.quantitySlot">
						<div class="col-12 centered align-end">
							<AppSwitchTriple class="mb-10"
								iconLeft="iconLess"
								iconCenter="iconEqual"
								iconRight="iconGreater"
								:active-item="filter.activeItem"
								@switched="v=>{ filters[index].activeItem = v; }"
							/>
							<AppNumberInput
								:placeholder="filter.text"
								:value="numberInputValue(filter)"
								:symbol="filter.priceSlot ? currencyStore.currCurrency.symbol : ''"
								:double-decimal="!!filter.priceSlot"
								@changed-value="v=>{ filter.callbackValue(v, index); }"
							/>
						</div>
					</slot>
					<slot v-if="filter.dateSlot">
						<div class="col-12 mt-5">
							<DatePicker color="teal"
										:is-dark="userStore.darkMode"
										v-model.range="filter.value"
										is24hr
										:mode="filter.mode(index)"
										@update:modelValue="value => filter.callbackValue(value, index)"
										:max-date="new Date()"
							/>
						</div>
					</slot>
				</AppFilter>
			</div>
		</div>
		<div class="button-wrapper">
			<div @click="resetFilters" class="reset-filters-cta">
				<img alt="icon erase filters" :src="'https://img.qromo.io/img/icons/icon-trash'+(userStore.darkMode ? '-dark' : '')+'.svg'">
				<p>{{getString('erase_filters')}}</p>
			</div>
		</div>
	</div>

</template>

<script>
import AppFilter from "@/shared/components/AppFilter.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import AppSwitchTriple from "@/shared/components/AppSwitchTriple.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppButton from "@/shared/components/AppButton.vue";

import {formatDateTime, formatDateTimeWithSeconds} from "@/shared/helpers/dates";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import {DatePicker} from "v-calendar";

export default {
	setup() {
		const userStore = useUserStore();
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, formatDateTime, currencyStore, userStore, languageStore};
	},
	name: "AppFilterGroup",
	components: {
		DatePicker,
		AppFilter,
		AppTextInput,
		AppSelect,
		AppChecklist,
		AppSwitchTriple,
		AppNumberInput,
		AppButton,
	},
	emits: ['changed-filter', 'filter-clicked'],
	data() {
		return {
			baseFiltersProps: {
				amount: {
					callbackValueQuantity: (v, index) => { this.filters[index].value = this.currencyStore.actualToDbValue(v) },
					getTextQuantity: (index) => {
						if (this.filters[index].active) {
							let comparison = this.filters[index].activeItem === 0 ? "<" : "=";
							comparison = this.filters[index].activeItem === 2 ? ">" : comparison;
							return comparison + ' ' + this.currencyStore.readableCurrencyValue(this.filters[index].value)
						}
						return this.filters[index].text;
					},
					callbackValue: (v, index) => { this.filters[index].value = v },
					callbackApply: (index) => {
						this.filters[index].active = !!this.filters[index].value
						this.filters[index].finalValue = this.computedFilters[index].getText(index);
					},
					reset: (index) => {
						this.filters[index].value = null
						this.filters[index].active = false
						this.filters[index].finalValue = ''
						this.filters[index].activeItem = 0
					},
					getText: (index) => {
						if (this.filters[index].active) {
							let comparison = this.filters[index].activeItem === 0 ? "<" : "=";
							comparison = this.filters[index].activeItem === 2 ? ">" : comparison;
							return comparison + ' ' + this.filters[index].value
						}
						return this.filters[index].text;
					},
					getValue: (index) => {
						let val = this.filters[index].value;
						if(!this.filters[index].active || !val) { return null }
						let comparison = this.filters[index].activeItem === 0 ? "<" : "";
						comparison = this.filters[index].activeItem === 2 ? ">" : comparison;
						return comparison+val;
					}
				},
				checklist: {
					checklistSlot: true,
					callbackValue: (v, index) => {
						this.filters[index].selectedIndexes = v
					},
					callbackApply: (index) => {
						this.filters[index].active = !!this.filters[index].selectedIndexes.length
						this.filters[index].finalValue = this.computedFilters[index].getText(index);
					},
					reset: (index) => {
						this.filters[index].active = false
						this.filters[index].selectedIndexes = []
						this.filters[index].finalValue = ''
					},
					getValue: (index) => {
						let val = this.filters[index].selectedIndexes;
						if(!this.filters[index].active || !val || !val.length || val.length === this.filters[index].values.length) { return null }
						let returning = [];
						let keysArray = this.filters[index].keysArray;
						val.forEach(selectedIndex=> {
							returning.push(keysArray[selectedIndex])
						})
						return returning;
					},
					getText: (index) => {
						if (this.filters[index].active) {
							let returning = "";
							let valuesArray = this.filters[index].values;
							this.filters[index].selectedIndexes.forEach(selectedIndex => {
								returning += returning ? ", " : "";
								returning += valuesArray[selectedIndex];
							})
							return returning;
						}
						return this.filters[index].text;
					},
				},
				/*date: {
					selectSlot: true,
					callbackValue: (v, index) => {
						this.filters[index].selectedIndex = v
						this.filters[index].days = this.filters[index].daysArray[v]
					},
					callbackApply: (index) => {
						this.filters[index].active = !!this.filters[index].days
						this.filters[index].finalValue = this.computedFilters[index].getText(index);
					},
					reset: (index) => {
						this.filters[index].days = null
						this.filters[index].active = false
						this.filters[index].finalValue = ''
						this.filters[index].selectedIndex = null
					},
					getValue: (index) => {
						let val = this.filters[index].selectedIndex;
						if(!this.filters[index].active || val === null) { return null }
						let finalDate = new Date();
						finalDate.setDate(finalDate.getDate() - this.filters[index].days);
						return '>'+this.formatDateTime(finalDate);
					},
					getText: (index) => {
						if (this.filters[index].active) {
							return this.filters[index].values[this.filters[index].selectedIndex];
						}
						return this.filters[index].text;
					},
				},*/
				text: {
					textSlot: true,
					callbackValue: (v, index) => {
						this.filters[index].value = v
					},
					callbackApply: (index) => {
						this.filters[index].active = !!this.filters[index].value
						this.filters[index].finalValue = this.computedFilters[index].getText(index);
					},
					reset: (index) => {
						this.filters[index].value = null
						this.filters[index].active = false
						this.filters[index].finalValue = ''
					},
					getValue: (index) => {
						let val = this.filters[index].value;
						if(!this.filters[index].active || !val) { return null }
						return "%"+val+"%";
					},
					getText: (index) => {
						if (this.filters[index].active) {
							return this.filters[index].value;
						}
						return this.filters[index].text;
					},
				},
				exactMatch: {
					textSlot: true,
					callbackValue: (v, index) => {
						this.filters[index].value = v
					},
					callbackApply: (index) => {
						this.filters[index].active = !!this.filters[index].value
						this.filters[index].finalValue = this.computedFilters[index].getText(index);
					},
					reset: (index) => {
						this.filters[index].value = null
						this.filters[index].active = false
						this.filters[index].finalValue = ''
					},
					getValue: (index) => {
						let val = this.filters[index].value;
						if(!this.filters[index].active || !val) { return null }
						return val;
					},
					getText: (index) => {
						if (this.filters[index].active) {
							return this.filters[index].value;
						}
						return this.filters[index].text;
					},
				},
				date: {
					dateSlot: true,
					value: {
						start: new Date(),
						end: new Date(),
					},
					callbackValue: (v, index) => {
						this.filters[index].value = v
					},
					callbackApply: (index) => {
						this.filters[index].active = !!this.filters[index]?.value?.start && !!this.filters[index]?.value?.end;
						this.filters[index].finalValue = this.computedFilters[index].getText(index);
					},
					reset: (index) => {
						this.filters[index].value = {start: new Date(), end: new Date()}
						this.filters[index].active = false
						this.filters[index].finalValue = ''
					},
					getValue: (index) => {
						let val = this.filters[index].value;
						if(!this.filters[index].active || !val || !val.start || !val.end) { return null }
						return val;
					},
					getStartValue: (index) => {
						let val = this.filters[index].value;
						if(!this.filters[index].active || !val || !val.start) { return null }
						if(!this.filters[index].hasOwnProperty('dateTime')){
							val.start.setHours(0, 0, 0);
						}
						return ">" + formatDateTimeWithSeconds(val.start);
					},
					getEndValue: (index) => {
						let val = this.filters[index].value;
						if(!this.filters[index].active || !val || !val.end) { return null }
						if(!this.filters[index].hasOwnProperty('dateTime')){
							val.end.setHours(23, 59, 59);
						}
						return "<" + formatDateTimeWithSeconds(val.end);
					},
					mode: index => {
						if (this.filters[index].hasOwnProperty('dateTime')) {
							return 'dateTime';
						}
						return 'date';
					},
					getText: (index) => {
						if (this.filters[index].active) {
							let { start: startDateDB, end: endDateDB } = this.filters[index].value;
							let fullYear = endDateDB.getFullYear() !== startDateDB.getFullYear() ||
								endDateDB.getFullYear() !== new Date().getFullYear();
							let sameDate = endDateDB.toDateString() === startDateDB.toDateString();
							let today = endDateDB.toDateString() === new Date().toDateString();

							return this.localeDateString(startDateDB, fullYear) + ' - ' +
								(sameDate && today ? this.getString('today') : this.localeDateString(endDateDB, fullYear));
						}
						return this.filters[index].text;
					},
				}
			}
		};
	},
	props: {
		filters: {
			type: Array,
			default: [],
			validator(value) {
				return value === null || value.length === 0 || value.every(item => item.text && item.type);
			},
		},
	},
	computed: {
		computedFilters() {
			let returning = [];
			if(!this.filters || !this.filters.length) { return returning; }
			this.filters.forEach(filter=>{
				if(this.baseFiltersProps[filter.type]) {
					let finalFilter = {...filter, ...this.baseFiltersProps[filter.type]};
					returning.push(finalFilter);
				}
				else if(['price', 'quantity'].includes(filter.type)) {
					let finalFilter = {...filter, ...this.baseFiltersProps['amount']};
					if(filter.type === 'price') {
						finalFilter.getText = finalFilter.getTextQuantity;
						finalFilter.callbackValue = finalFilter.callbackValueQuantity;
						finalFilter.priceSlot = true;
					}
					else if(filter.type === 'quantity') {
						finalFilter.quantitySlot = true;
					}
					returning.push(finalFilter);
				}
				else {
					returning.push(filter);
				}
			});
			return returning;
		},
	},
	methods: {
		numberInputValue(filter) {
			if(filter.priceSlot) {
				return this.currencyStore.dbToActualValue(filter.value)
			}
			return Number(filter.value || 0);
		},
		resetFilters() {
			this.computedFilters.forEach((filter, index) => { filter.reset(index) })
			this.$emit('changed-filter', null);
		},
		applyFilter(index) {
			if(this.computedFilters[index].hasOwnProperty('callbackApply')) {
				this.computedFilters[index].callbackApply(index);
			}
			this.$emit('changed-filter', this.getAllValues());
		},
		resetFilter(index) {
			if(this.computedFilters[index].hasOwnProperty('reset')) {
				this.computedFilters[index].reset(index);
			}
			this.$emit('changed-filter', this.getAllValues());
		},
		getAllValues() {
			let returning = null;
			this.computedFilters.forEach((filter, index) => {
				if (filter.hasOwnProperty('getValue')) {
					if (filter.type === 'date') {
						let val = filter.getValue(index);
						if(val) {
							if(!returning) { returning = {} }
							returning[filter.key] = filter.getStartValue(index);
							returning[filter.tableName + "." + filter.key] = filter.getEndValue(index);
						}
					}
					else {
						let val = filter.getValue(index);
						if(val) {
							if(!returning) { returning = {} }
							returning[filter.key] = val;
						}
					}
				}
			})
			return returning;
		},
		filterClicked(index) {
			if(!this.computedFilters[index].toggleCloudPopup) {
				this.computedFilters[index].callbackFilterClick(index);
			}
		},
		localeDateString(date, fullYear = false) {
			let options = {month: 'long', day: 'numeric'};
			if(fullYear) {
				options.year = 'numeric';
				options.month = 'short';
			}
			return date.toLocaleDateString([this.languageStore.lang || 'en-US'], options);
		},
	}
}

</script>

<style lang="scss" scoped>

.filter-group {
	justify-content: space-between;
	align-items: center;
	padding: 0 10px;

	.col-buttons {
		@include col(grow);

		@media (max-width: 480px) {
			flex: 0 0 100%;
			max-width: 100%;
		}
	}
	.filter-wrapper {
		margin-bottom: 3px;
	}
	.button-wrapper {
		margin-left: auto;
	}
}
.reset-filters-cta {
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	user-select: none;

	img {
		width: 15px;
		margin-right: 3px;
	}
	p {
		color: $primary;
		font-size: 12px;
		line-height: 14px;
	}
	& * {
		pointer-events: none;
		user-select: none;
	}
}

</style>